/* Cart drawer: add to cart, view cart and edit quantities without a page
 * change. Strictly an enhancement — every path it intercepts still works
 * when this file fails to load, and it removes nothing from the DOM.
 *
 * It leans on <dialog>.showModal() for the focus trap, Escape handling and
 * inert background rather than reimplementing them.
 */
(() => {
  const DRAWER = '[data-notch-cart-drawer]';
  const SECTION_ID = 'notch-cart-drawer';

  const drawer = () => document.querySelector(DRAWER);
  const root = () => window.Shopify?.routes?.root || '/';

  // The drawer's markup is replaced wholesale from the server, so listeners
  // are delegated from the dialog element, never bound to its children.
  const setError = (message) => {
    const box = drawer()?.querySelector('[data-notch-drawer-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  };

  const open = () => {
    const el = drawer();
    if (!el) return;
    if (!el.open) el.showModal();
  };

  // The count bubble is rendered conditionally by the header and its label is
  // pluralised by Liquid, so both come back from the server rather than being
  // rebuilt here — that keeps the count correct AND the wording translatable.
  const refresh = async () => {
    const link = document.querySelector('[data-notch-cart-link]');
    const headerId = link?.dataset.notchCartSection;
    const want = headerId ? `${SECTION_ID},${headerId}` : SECTION_ID;
    const response = await fetch(`${root()}?sections=${want}`);
    if (!response.ok) return;
    const data = await response.json();
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    if (data[SECTION_ID]) {
      const content = parse(data[SECTION_ID]).querySelector('[data-notch-drawer-content]');
      const current = drawer()?.querySelector('[data-notch-drawer-content]');
      if (content && current) current.replaceWith(content);
    }

    if (headerId && data[headerId] && link) {
      const fresh = parse(data[headerId]).querySelector('[data-notch-cart-link]');
      if (fresh) link.innerHTML = fresh.innerHTML;
    }
  };

  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || '');
    return data;
  };

  const addToCart = async (form) => {
    // The form's own action is /cart/add, which answers with a redirect to a
    // page. The .js endpoint answers with JSON, which is what a drawer needs.
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || '');
    return data;
  };

  const onSubmit = async (event) => {
    const form = event.target.closest('[data-notch-add-form]');
    if (!form || !drawer()) return;
    event.preventDefault();
    setError('');
    try {
      await addToCart(form);
      await refresh();
      open();
    } catch (error) {
      // Anything unexpected falls back to the page that always works.
      console.error('[notch cart]', error);
      if (error?.message) { setError(error.message); open(); } else { form.submit(); }
    }
  };

  const changeLine = async (line, quantity) => {
    setError('');
    try {
      await post(`${root()}cart/change.js`, { line, quantity });
      await refresh();
    } catch (error) {
      setError(error.message);
    }
  };

  const onClick = (event) => {
    const el = drawer();
    if (!el) return;

    const cartLink = event.target.closest('[data-notch-cart-link]');
    if (cartLink) { event.preventDefault(); open(); return; }

    if (event.target.closest('[data-notch-drawer-close]')) { el.close(); return; }

    const remove = event.target.closest('[data-notch-line-remove]');
    if (remove && el.contains(remove)) {
      event.preventDefault();
      changeLine(Number(remove.dataset.notchLineRemove), 0);
    }
  };

  const onChange = (event) => {
    const input = event.target.closest('[data-notch-line-index]');
    if (!input || !drawer()?.contains(input)) return;
    changeLine(Number(input.dataset.notchLineIndex), Number(input.value));
  };

  // Clicking the backdrop closes: <dialog> reports clicks outside the box
  // as clicks on the element itself.
  const onDialogClick = (event) => {
    if (event.target === drawer()) drawer().close();
  };

  const start = () => {
    if (document.documentElement.dataset.notchCartReady) return;
    document.documentElement.dataset.notchCartReady = 'true';
    document.addEventListener('submit', onSubmit);
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('click', onDialogClick, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
